import { useEffect, useState } from 'react'
import type { ContratoParcela, ParcelaCondicao } from '@services/db.js'
import type { NovaParcela } from '@services/gestao.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { Alert } from '@components/primitives/Alert.js'
import { CONDICOES } from './contratosLabels.js'

interface Props {
  open: boolean
  initial?: ContratoParcela | null
  onClose: () => void
  onSalvar: (data: NovaParcela) => Promise<void>
  onEditar?: (id: number, patch: Partial<NovaParcela>) => Promise<void>
}

const EMPTY = {
  descricao: '',
  valor: 0,
  condicao: 'medicao' as ParcelaCondicao,
  dataPrevista: '',
  observacoes: '',
}

export function ParcelaFormModal({ open, initial, onClose, onSalvar, onEditar }: Props) {
  const [form, setForm] = useState(EMPTY)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(
      initial
        ? {
            descricao: initial.descricao,
            valor: initial.valor,
            condicao: initial.condicao,
            dataPrevista: initial.dataPrevista ?? '',
            observacoes: initial.observacoes ?? '',
          }
        : EMPTY,
    )
  }, [open, initial])

  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const condicao = CONDICOES.find((c) => c.value === form.condicao)
  const canSubmit = form.descricao.trim().length > 1 && form.valor > 0

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return
    setSalvando(true)
    if (initial && onEditar) {
      await onEditar(initial.id!, {
        descricao: form.descricao.trim(),
        valor: form.valor,
        condicao: form.condicao,
        dataPrevista: form.dataPrevista || undefined,
        observacoes: form.observacoes.trim() || undefined,
      })
    } else {
      await onSalvar({
        ordem: 0,
        descricao: form.descricao.trim(),
        valor: form.valor,
        condicao: form.condicao,
        ...(form.dataPrevista ? { dataPrevista: form.dataPrevista } : {}),
        ...(form.observacoes.trim() ? { observacoes: form.observacoes.trim() } : {}),
      })
    }
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title={initial ? 'Editar parcela' : 'Nova parcela ou aditivo'}
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='parcela-form'
            loading={salvando}
            disabled={!canSubmit}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <form id='parcela-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        <Input
          label='Descrição'
          value={form.descricao}
          placeholder='Ex.: Laje do térreo concretada'
          required
          onChange={(e) => set('descricao', e.target.value)}
        />
        <div className='cf3-gestao-form__row'>
          <MoneyInput label='Valor' value={form.valor} required onChange={(v) => set('valor', v)} />
          <Input
            label='Data prevista'
            type='date'
            value={form.dataPrevista}
            onChange={(e) => set('dataPrevista', e.target.value)}
          />
        </div>

        <div className='cf-field'>
          <span className='cf-field__label'>Condição de pagamento</span>
          <div className='cf3-chip-row' role='radiogroup' aria-label='Condição de pagamento'>
            {CONDICOES.map((c) => (
              <button
                key={c.value}
                type='button'
                role='radio'
                aria-checked={form.condicao === c.value}
                className={`cf3-chip${form.condicao === c.value ? ' cf3-chip--active' : ''}`}
                onClick={() => set('condicao', c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>
          {condicao && <span className='cf-field__hint'>{condicao.hint}</span>}
        </div>

        {form.condicao === 'aditivo' && (
          <Alert
            variant='warning'
            title='Aditivo aumenta o valor do contrato'
            message='Peça o motivo por escrito (WhatsApp vale) antes de aceitar. Aditivos sem registro são a maior fonte de estouro de orçamento.'
          />
        )}

        <Textarea
          label='Observações'
          value={form.observacoes}
          rows={2}
          placeholder='Critério de aceite, o que precisa estar pronto…'
          onChange={(e) => set('observacoes', e.target.value)}
        />
      </form>
    </Modal>
  )
}
